import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import {
  Science as ScienceIcon,
  Schedule as ScheduleIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  Storage as StorageIcon,
  GitHub as GitHubIcon,
} from '@mui/icons-material';
import versionInfo from '../version.json';

const About = () => {
  const [serverVersion, setServerVersion] = useState('');

  useEffect(() => {
    const fetchServerVersion = async () => {
      try {
        const response = await fetch('/api/health');
        if (response.ok) {
          const data = await response.json();
          if (data?.data?.version) {
            setServerVersion(data.data.version);
          }
        }
      } catch (error) {
        console.error('Error fetching server version:', error);
      }
    };
    fetchServerVersion();
  }, []);

  const features = [
    {
      icon: <ScheduleIcon fontSize="large" color="primary" />,
      title: 'Intelligent Scheduling',
      description: 'Advanced dispatch scheduling algorithm that optimizes observation time across multiple observatories with priority-based queue management.',
    },
    {
      icon: <CloudIcon fontSize="large" color="primary" />,
      title: 'Cloud-Based Coordination',
      description: 'Centralized server coordinates observations across geographically distributed observatories for maximum sky coverage.',
    },
    {
      icon: <SecurityIcon fontSize="large" color="primary" />,
      title: 'Role-Based Access',
      description: 'Comprehensive permission system supporting server admins, observatory owners, observatory admins, and regular users.',
    },
    {
      icon: <SpeedIcon fontSize="large" color="primary" />,
      title: 'Real-Time Updates',
      description: 'WebSocket-based communication provides instant status updates as observations progress through the queue.',
    },
    {
      icon: <GroupIcon fontSize="large" color="primary" />,
      title: 'Multi-User Support',
      description: 'Support for organizations, projects, and collaborative observation campaigns with fair scheduling algorithms.',
    },
    {
      icon: <StorageIcon fontSize="large" color="primary" />,
      title: 'FITS File Management',
      description: 'Automatic capture and storage of FITS files with MinIO object storage and comprehensive file tracking.',
    },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 4, mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
            <ScienceIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h3" component="h1">
              Asterism
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Professional Observatory Automation System
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Chip label={`GUI v${versionInfo.version}`} variant="outlined" />
            {serverVersion && <Chip label={`Server v${serverVersion}`} variant="outlined" />}
          </Box>
        </Paper>

        {/* Overview */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Asterism is a comprehensive observatory automation system designed for
            professional and serious amateur astronomers. It coordinates observations across multiple
            observatories, optimizing telescope time and enabling collaborative science campaigns.
          </Typography>
          <Typography variant="body1" paragraph>
            The system integrates seamlessly with N.I.N.A. (Nighttime Imaging 'N' Astronomy) through
            a dedicated plugin, providing automated observation execution while the central server
            handles scheduling, queue management, and data storage.
          </Typography>
        </Paper>

        {/* Features */}
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Key Features
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {feature.icon}
                    <Typography variant="h6">{feature.title}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* System Components */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            System Components
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom color="primary">
                NINA Plugin
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Windows-based plugin for N.I.N.A. that executes observations, controls equipment,
                and communicates with the central server via WebSocket.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom color="primary">
                Scheduler Server
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Node.js/TypeScript backend that manages the observation queue, coordinates multiple
                observatories, and provides REST/WebSocket APIs.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom color="primary">
                Web Interface
              </Typography>
              <Typography variant="body2" color="text.secondary">
                React-based GUI for observation submission, monitoring, and system administration
                with role-based access control.
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Links */}
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Resources
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GitHubIcon color="action" />
              <Link
                href="https://first-light-systems.github.io/nina-scheduler-docs/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Documentation
              </Link>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              &copy; {new Date().getFullYear()} First Light Observatory Systems LLC. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default About;
